import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from '../../lib/axios';
import RichTextEditor from '../email/RichTextEditor';
import { formatMoney } from '../../lib/quoteTotals';

/**
 * Envío de la cotización al cliente con el PDF adjunto. Se apoya en el módulo
 * de correo: mismas cuentas conectadas y misma firma por cuenta.
 */
export default function SendQuoteModal({ quote, onClose, onSent }) {
    const [to, setTo] = useState(quote.contact?.email || '');
    const [accountId, setAccountId] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [error, setError] = useState(null);

    const { data: accountsResp } = useQuery({
        queryKey: ['email-accounts'],
        queryFn: () => axios.get('/api/email-accounts'),
    });
    const accounts = accountsResp?.data || [];

    useEffect(() => {
        const nombre = quote.contact?.full_name?.split(' ')[0] || '';
        setSubject(`Cotización ${quote.number}`);
        setBody(
            `<p>Estimado/a ${nombre},</p>` +
            `<p>Le adjunto la cotización <strong>${quote.number}</strong> por un total de ` +
            `<strong>${formatMoney(quote.total, quote.currency)}</strong>.</p>` +
            `<p>Quedo a su disposición para cualquier consulta.</p>`
        );
    }, [quote]);

    const sendMutation = useMutation({
        mutationFn: () => axios.post(`/api/quotes/${quote.id}/send`, {
            to,
            subject,
            body_html: body,
            account_id: accountId || undefined,
        }),
        onSuccess: () => onSent?.(),
        onError: (e) => setError(e.response?.data?.message || 'No se pudo enviar la cotización.'),
    });

    const sinCuentas = accounts.length === 0;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="font-black dark:text-white">Enviar cotización</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Se adjunta el PDF de {quote.number} · {formatMoney(quote.total, quote.currency)}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {sinCuentas && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <span className="material-symbols-outlined text-amber-600 text-lg">warning</span>
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                            No tenés ninguna cuenta de correo conectada. Agregá una en Configuración
                            para poder enviar cotizaciones.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{error}</div>
                )}

                <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                    className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white">
                    <option value="">Cuenta predeterminada</option>
                    {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.display_name || a.email_address}</option>
                    ))}
                </select>

                <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
                    placeholder="Correo del destinatario"
                    className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />

                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="Asunto"
                    className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white" />

                <RichTextEditor value={body} onChange={setBody} placeholder="Mensaje" />

                <p className="text-xs text-gray-400 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">attach_file</span>
                    Se adjunta automáticamente el PDF. Tu firma se agrega al pie.
                </p>

                <div className="flex justify-end gap-2 pt-1">
                    <button onClick={onClose} className="py-2 px-4 text-sm font-bold text-gray-500">Cancelar</button>
                    <button
                        onClick={() => { setError(null); sendMutation.mutate(); }}
                        disabled={sendMutation.isPending || sinCuentas || !to || !subject}
                        className="py-2 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50"
                    >
                        {sendMutation.isPending ? 'Enviando...' : 'Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
